import { useState, useCallback, useRef, useEffect } from 'react';
import { toasterApi } from '@terra/ui-design-system';
import { useReplayStore } from '../store/replay-store';
import {
  useReplaySessionStore,
  type ReplaySession,
} from '../store/replaySessionStore';
import { useApplicationScriptConfig } from '../store/preRequestScriptStore';
import {
  formatRequestToText,
  parseRequestFromText,
} from '../utils/httpFormatter';
import {
  getHttp1TerminatorError,
  getHttp1PseudoHeaderError,
} from '../utils/httpRequestValidation';
import {
  replayProtocolFromHttpVersion,
  type StoredHarDisplayEntry,
} from '@terra/shared-utils';
import { registerReplayTrigger } from './useReplayKeyboard';
import type { BodyEncoding } from '../electron-shared/utils/binary-body';
import type { CredentialWithRequest } from '../components/replay/CredentialToolbarDropdown';
import { useApplicationCredentials } from './useApplicationCredentials';
import type { ReplayMtlsPayload, ReplayResponse } from '../types/replay';
import {
  MAX_AUTO_REDIRECTS,
  buildRedirectRequest,
  getLocationHeader,
  isRedirectStatus,
} from '../utils/replayRedirect.utils';
import { resolveGrpcReplaySendBody } from '../utils/replayGrpcDisplay.utils';
import { isGrpcHarEntry } from '../utils/harGrpcDisplay.utils';
import { buildReplayGrpcHarDisplayFromSession } from '../utils/replayOpenRequest.utils';

const ABORTED_MESSAGE = 'Request aborted by user';

function resolveReplayBody({
  session,
  parsedBody,
}: {
  session: ReplaySession;
  parsedBody: string;
}): { body: string; bodyEncoding: BodyEncoding | undefined } {
  if (!parsedBody) {
    return { body: '', bodyEncoding: undefined };
  }

  if (session.request.bodyEncoding === 'base64') {
    return { body: session.request.body || '', bodyEncoding: 'base64' };
  }

  return { body: parsedBody, bodyEncoding: undefined };
}

type SendSingleRequestResult = {
  response: ReplayResponse;
  sentRequestWire: string;
  sentRequestWireEncoding?: BodyEncoding;
};

function resolveGrpcHarDisplayForSend(
  session: ReplaySession,
): StoredHarDisplayEntry | undefined {
  const candidate =
    session.grpcHarDisplay ??
    buildReplayGrpcHarDisplayFromSession({
      session,
      currentResponse:
        session.currentResponseIndex >= 0
          ? session.responseHistory[session.currentResponseIndex]
          : undefined,
    });

  if (!candidate || !isGrpcHarEntry(candidate)) {
    return undefined;
  }

  return candidate;
}

type ReplayResponseEntry = ReplaySession['responseHistory'][0];

function toResponseHistoryEntry({
  replayResponse,
  requestData,
  credential,
  sentRequestWire,
  sentRequestWireEncoding,
}: {
  replayResponse: ReplayResponse;
  requestData: string;
  credential?: { id: string; username: string; role: string };
  sentRequestWire?: string;
  sentRequestWireEncoding?: BodyEncoding;
}): ReplayResponseEntry {
  return {
    statusCode: replayResponse.statusCode,
    statusMessage: replayResponse.statusMessage,
    headers: replayResponse.headers,
    rawHeaders: replayResponse.rawHeaders,
    body: replayResponse.body,
    duration: replayResponse.duration,
    error: replayResponse.error,
    httpVersion: replayResponse.httpVersion,
    timestamp: new Date(),
    requestData,
    credential,
    sentRequestWire,
    sentRequestWireEncoding,
  };
}

function createErrorResponse({
  error,
  requestData,
  credential,
}: {
  error: string;
  requestData: string;
  credential?: { id: string; username: string; role: string };
}): ReplayResponseEntry {
  return {
    statusCode: 0,
    statusMessage: 'Error',
    headers: {},
    body: '',
    duration: 0,
    error,
    timestamp: new Date(),
    requestData,
    credential,
  };
}

type PreRequestSandboxStatus = {
  ready: boolean;
  platform: string;
  sandboxName?: 'macos-seatbelt' | 'linux-bwrap' | 'windows-wsl-bwrap';
  reason?: string;
  allowUnsafeFallback: boolean;
  isPackaged: boolean;
};

type SandboxCredentialData = {
  username: string;
  password: string;
  role: string;
  secrets: Record<string, string>;
  headers: Record<string, string>;
  cookies: Record<string, string>;
};

function toKeyValueRecord(
  items: { key: string; value: string }[],
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const item of items) {
    result[item.key] = item.value;
  }

  return result;
}

function toNameValueRecord(
  items: { name: string; value: string }[],
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const item of items) {
    result[item.name] = item.value;
  }

  return result;
}

function buildSandboxCredential(
  credential: import('./useApplicationCredentials').ApplicationCredential,
): SandboxCredentialData {
  return {
    username: credential.username,
    password: credential.password ?? '',
    role: credential.role,
    secrets: toKeyValueRecord(credential.secrets),
    headers: toKeyValueRecord(credential.headers),
    cookies: toNameValueRecord(credential.cookies),
  };
}

function buildReplayMtlsPayload(
  credential:
    | import('./useApplicationCredentials').ApplicationCredential
    | undefined,
): ReplayMtlsPayload | undefined {
  if (!credential) {
    return undefined;
  }

  const hasPfx = !!credential.mtlsPfx;
  const hasCertAndKey = !!credential.mtlsCert && !!credential.mtlsKey;

  if (!hasPfx && !hasCertAndKey) {
    return undefined;
  }

  return {
    cert: credential.mtlsCert,
    key: credential.mtlsKey,
    pfx: credential.mtlsPfx,
    passphrase: credential.mtlsPassphrase,
    origin: credential.mtlsOrigin,
  };
}

export function useReplayRequest({ sessionId }: { sessionId: string }) {
  const { sendReplayRequest } = useReplayStore();

  const session = useReplaySessionStore((state) =>
    state.sessions.find((s) => s.id === sessionId),
  );

  const updateSessionRequest = useReplaySessionStore(
    (s) => s.updateSessionRequest,
  );
  const addResponseToSession = useReplaySessionStore(
    (s) => s.addResponseToSession,
  );
  const setSessionResponseIndex = useReplaySessionStore(
    (s) => s.setCurrentResponseIndex,
  );
  const updateSessionProtocol = useReplaySessionStore(
    (s) => s.updateSessionProtocol,
  );
  const updateSessionPreserveContentLength = useReplaySessionStore(
    (s) => s.updateSessionPreserveContentLength,
  );
  const updateSessionConnectionTarget = useReplaySessionStore(
    (s) => s.updateSessionConnectionTarget,
  );
  const updateSessionActiveCredential = useReplaySessionStore(
    (s) => s.updateSessionActiveCredential,
  );
  const updateSessionAutoFollowRedirects = useReplaySessionStore(
    (s) => s.updateSessionAutoFollowRedirects,
  );
  const updateSessionSyncTargetWithHostHeader = useReplaySessionStore(
    (s) => s.updateSessionSyncTargetWithHostHeader,
  );

  const { credentials: applicationCredentials } = useApplicationCredentials({
    applicationId: session?.applicationId,
    enabled: !!session?.applicationId,
  });

  const applicationCredentialsRef = useRef(applicationCredentials);

  applicationCredentialsRef.current = applicationCredentials;

  const sessionRef = useRef(session);

  sessionRef.current = session;

  const {
    activePreset,
    script: appScript,
    variables: scriptVariables,
    setActivePresetId,
    setActivePresetVariables,
  } = useApplicationScriptConfig(session?.applicationId);
  const scriptEnabled = !!activePreset;

  const [isLoading, setIsLoading] = useState(false);
  const [preRequestSandboxStatus, setPreRequestSandboxStatus] =
    useState<PreRequestSandboxStatus | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);

  const runPreRequestScript = useCallback(
    async ({
      parsedRequest,
      variablesOverride,
      shouldPersistVariables,
      credentials: credentialData,
    }: {
      parsedRequest: {
        method: string;
        url: string;
        headers: Record<string, string | string[]>;
        body: string;
        httpVersion?: string;
      };
      variablesOverride?: Record<string, string>;
      shouldPersistVariables?: () => boolean;
      credentials?: SandboxCredentialData;
    }) => {
      const variablesForExecution = variablesOverride ?? scriptVariables;
      const script = appScript?.trim();

      if (!script || !scriptEnabled)
        return {
          request: parsedRequest,
          logs: [],
          variables: variablesForExecution,
        };

      const isSandboxUnavailableWithoutFallback =
        preRequestSandboxStatus?.ready === false &&
        !preRequestSandboxStatus.allowUnsafeFallback;

      if (isSandboxUnavailableWithoutFallback) {
        return {
          request: parsedRequest,
          logs: [
            `[warn] Pre-request script skipped: ${preRequestSandboxStatus.reason || 'Sandbox unavailable'}`,
          ],
          variables: variablesForExecution,
        };
      }

      if (
        typeof window === 'undefined' ||
        !window.electronAPI?.replay?.executePreRequestScript
      ) {
        return {
          request: parsedRequest,
          logs: ['[error] Electron API not available for script execution'],
          variables: variablesForExecution,
        };
      }

      const result = await window.electronAPI.replay.executePreRequestScript({
        script,
        request: {
          method: parsedRequest.method,
          url: parsedRequest.url,
          headers: parsedRequest.headers,
          body: parsedRequest.body,
        },
        variables: variablesForExecution,
        credentials: credentialData,
      });
      const nextVariables = result.variables ?? variablesForExecution;
      const canPersistVariables = shouldPersistVariables?.() ?? true;

      if (result.success && result.request) {
        if (canPersistVariables) {
          setActivePresetVariables(nextVariables);
        }

        return {
          request: {
            ...result.request,
            httpVersion: parsedRequest.httpVersion,
          },
          logs: result.logs,
          variables: nextVariables,
        };
      }

      throw new Error(result.error || 'Pre-request script failed');
    },
    [
      appScript,
      preRequestSandboxStatus,
      scriptEnabled,
      scriptVariables,
      setActivePresetVariables,
    ],
  );

  const sendSingleRequest = useCallback(
    async ({
      session: sess,
      parsedRequest,
      requestData,
      originalUrl,
      name,
      abortController,
      replayMtls,
    }: {
      session: ReplaySession;
      parsedRequest: {
        method: string;
        url: string;
        headers: Record<string, string | string[]>;
        body: string;
      };
      requestData: string;
      originalUrl: string;
      name: string;
      abortController?: AbortController;
      replayMtls?: ReplayMtlsPayload;
    }): Promise<SendSingleRequestResult> => {
      const grpcHarDisplay = resolveGrpcHarDisplayForSend(sess);

      let replayBody: string;
      let bodyEncoding: BodyEncoding | undefined;

      if (grpcHarDisplay && sess.applicationId) {
        const resolved = await resolveGrpcReplaySendBody({
          applicationId: sess.applicationId,
          grpcHarDisplay,
          requestData,
          originalUrl,
          parsedBody: parsedRequest.body,
          storedWireBody: sess.request.body,
          storedWireBodyEncoding: sess.request.bodyEncoding,
        });

        replayBody = resolved.body;
        bodyEncoding = resolved.bodyEncoding;
      } else {
        const resolved = resolveReplayBody({
          session: sess,
          parsedBody: parsedRequest.body,
        });

        replayBody = resolved.body;
        bodyEncoding = resolved.bodyEncoding;
      }

      const replayPromise = sendReplayRequest(
        {
          id: `replay-${Date.now()}`,
          name,
          method: parsedRequest.method,
          url: parsedRequest.url,
          headers: parsedRequest.headers,
          body: replayBody,
          bodyEncoding,
          timestamp: new Date(),
        },
        {
          protocol: sess.protocol,
          preserveContentLength: sess.preserveContentLength,
          connectionTarget: sess.connectionTarget,
          replayMtls,
        },
      ).then((response) => ({
        response,
        sentRequestWire: replayBody,
        sentRequestWireEncoding: bodyEncoding,
      }));

      if (abortController) {
        const abortPromise = new Promise<never>((_, reject) => {
          if (abortController.signal.aborted) {
            reject(new Error(ABORTED_MESSAGE));

            return;
          }

          abortController.signal.addEventListener(
            'abort',
            () => reject(new Error(ABORTED_MESSAGE)),
            { once: true },
          );
        });

        return Promise.race([replayPromise, abortPromise]);
      }

      return replayPromise;
    },
    [sendReplayRequest],
  );

  const handleReplay = useCallback(async () => {
    const sess = sessionRef.current;

    if (!sess) return;

    if (isLoadingRef.current && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      isLoadingRef.current = false;
      setIsLoading(false);

      return;
    }

    const requestData = sess.requestData;
    const originalUrl = sess.request.url;
    let historicalRequestData = requestData;

    const abortController = new AbortController();

    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const terminatorError = getHttp1TerminatorError({
        requestText: requestData,
      });

      if (terminatorError) {
        addResponseToSession(
          sessionId,
          createErrorResponse({
            error: terminatorError,
            requestData: historicalRequestData,
          }),
        );

        return;
      }

      const pseudoHeaderError = getHttp1PseudoHeaderError({
        requestText: requestData,
      });

      if (pseudoHeaderError) {
        addResponseToSession(
          sessionId,
          createErrorResponse({
            error: pseudoHeaderError,
            requestData: historicalRequestData,
          }),
        );

        return;
      }

      const parsedRequest = parseRequestFromText({
        text: requestData,
        originalUrl,
      });

      if (!parsedRequest) {
        addResponseToSession(
          sessionId,
          createErrorResponse({
            error: 'Failed to parse request',
            requestData: historicalRequestData,
          }),
        );

        return;
      }

      const parsedProtocol = replayProtocolFromHttpVersion(
        parsedRequest.httpVersion,
      );

      if (parsedProtocol !== sess.protocol) {
        updateSessionProtocol(sessionId, parsedProtocol);
      }

      const replaySession: ReplaySession = {
        ...sess,
        protocol: parsedProtocol,
      };

      const abortPromise = new Promise<never>((_, reject) => {
        if (abortController.signal.aborted) {
          reject(new Error(ABORTED_MESSAGE));

          return;
        }

        abortController.signal.addEventListener(
          'abort',
          () => reject(new Error(ABORTED_MESSAGE)),
          { once: true },
        );
      });

      const activeAppCredential = sess.activeCredential
        ? applicationCredentialsRef.current.find(
            (c) => c.id === sess.activeCredential?.id,
          )
        : undefined;
      const sandboxCredentials = activeAppCredential
        ? buildSandboxCredential(activeAppCredential)
        : undefined;
      const replayMtls = buildReplayMtlsPayload(activeAppCredential);

      const { request: finalRequest } = await Promise.race([
        runPreRequestScript({
          parsedRequest,
          credentials: sandboxCredentials,
          shouldPersistVariables: () =>
            !abortController.signal.aborted &&
            abortControllerRef.current === abortController,
        }),
        abortPromise,
      ]);

      historicalRequestData = formatRequestToText(finalRequest);

      if (abortController.signal.aborted) {
        throw new Error(ABORTED_MESSAGE);
      }

      const {
        response: replayResponse,
        sentRequestWire,
        sentRequestWireEncoding,
      } = await sendSingleRequest({
        session: replaySession,
        parsedRequest: finalRequest,
        requestData: historicalRequestData,
        originalUrl,
        name: 'Replay Request',
        abortController,
        replayMtls,
      });

      if (abortController.signal.aborted) return;

      const newResponse = toResponseHistoryEntry({
        replayResponse,
        requestData: historicalRequestData,
        sentRequestWire,
        sentRequestWireEncoding,
      });

      addResponseToSession(sessionId, newResponse);

      const currentSess = sessionRef.current;

      if (
        currentSess?.autoFollowRedirects &&
        isRedirectStatus(replayResponse.statusCode) &&
        !replayResponse.error
      ) {
        let currentResponse = replayResponse;
        let currentRequest = finalRequest;
        let redirectCount = 0;
        const maxRedirects = currentSess.maxRedirects ?? MAX_AUTO_REDIRECTS;

        while (
          isRedirectStatus(currentResponse.statusCode) &&
          redirectCount < maxRedirects &&
          !abortController.signal.aborted
        ) {
          const locationUrl = getLocationHeader(currentResponse.headers);

          if (!locationUrl) break;

          currentRequest = buildRedirectRequest({
            currentRequest,
            redirectUrl: locationUrl,
            statusCode: currentResponse.statusCode,
          });

          const redirectRequestData = formatRequestToText(currentRequest);

          updateSessionRequest(sessionId, redirectRequestData);

          const { request: scriptedRequest } = await runPreRequestScript({
            parsedRequest: currentRequest,
            credentials: sandboxCredentials,
          });

          const redirectProtocol = replayProtocolFromHttpVersion(
            currentRequest.httpVersion,
          );
          const redirectSession: ReplaySession = {
            ...(sessionRef.current ?? replaySession),
            protocol: redirectProtocol,
          };

          if (
            redirectProtocol !== (sessionRef.current ?? replaySession).protocol
          ) {
            updateSessionProtocol(sessionId, redirectProtocol);
          }

          const scriptedRequestData = formatRequestToText(scriptedRequest);

          const {
            response: redirectResponse,
            sentRequestWire,
            sentRequestWireEncoding,
          } = await sendSingleRequest({
            session: redirectSession,
            parsedRequest: scriptedRequest,
            requestData: scriptedRequestData,
            originalUrl,
            name: `Follow Redirect (${redirectCount + 1})`,
            abortController,
            replayMtls,
          });

          currentResponse = redirectResponse;

          addResponseToSession(
            sessionId,
            toResponseHistoryEntry({
              replayResponse: redirectResponse,
              requestData: scriptedRequestData,
              sentRequestWire,
              sentRequestWireEncoding,
            }),
          );

          redirectCount++;
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message === ABORTED_MESSAGE) {
        addResponseToSession(sessionId, {
          statusCode: 0,
          statusMessage: 'Cancelled',
          headers: {},
          body: '',
          duration: 0,
          error: 'Request cancelled by user',
          timestamp: new Date(),
          requestData: historicalRequestData,
        });

        return;
      }

      addResponseToSession(
        sessionId,
        createErrorResponse({
          error: error instanceof Error ? error.message : 'Unknown error',
          requestData: historicalRequestData,
        }),
      );
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [
    sessionId,
    addResponseToSession,
    sendSingleRequest,
    runPreRequestScript,
    updateSessionRequest,
    updateSessionProtocol,
  ]);

  const handleRunAllCredentials = useCallback(
    async (credentialsWithRequests: CredentialWithRequest[]) => {
      const sess = sessionRef.current;

      if (!sess || credentialsWithRequests.length === 0) return;
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      setIsLoading(true);

      try {
        let currentScriptVariables = scriptVariables;

        for (const { credential, requestText } of credentialsWithRequests) {
          let historicalRequestData = requestText;

          try {
            const terminatorError = getHttp1TerminatorError({ requestText });

            if (terminatorError) {
              throw new Error(terminatorError);
            }

            const pseudoHeaderError = getHttp1PseudoHeaderError({
              requestText,
            });

            if (pseudoHeaderError) {
              throw new Error(pseudoHeaderError);
            }

            const parsedRequest = parseRequestFromText({
              text: requestText,
              originalUrl: sess.request.url,
            });

            if (!parsedRequest) {
              throw new Error('Failed to parse request');
            }

            const parsedProtocol = replayProtocolFromHttpVersion(
              parsedRequest.httpVersion,
            );
            const currentSession = sessionRef.current ?? sess;

            if (parsedProtocol !== currentSession.protocol) {
              updateSessionProtocol(sessionId, parsedProtocol);
            }

            const replaySession: ReplaySession = {
              ...currentSession,
              protocol: parsedProtocol,
            };

            const appCredential = applicationCredentialsRef.current.find(
              (c) => c.id === credential.id,
            );
            const replayMtls = buildReplayMtlsPayload(appCredential);
            const { request: finalRequest, variables: nextVariables } =
              await runPreRequestScript({
                parsedRequest,
                variablesOverride: currentScriptVariables,
                credentials: appCredential
                  ? buildSandboxCredential(appCredential)
                  : undefined,
              });

            currentScriptVariables = nextVariables;
            historicalRequestData = formatRequestToText(finalRequest);

            const {
              response: replayResponse,
              sentRequestWire,
              sentRequestWireEncoding,
            } = await sendSingleRequest({
              session: replaySession,
              parsedRequest: finalRequest,
              requestData: historicalRequestData,
              originalUrl: sess.request.url,
              name: `Replay Request (${credential.username})`,
              replayMtls,
            });

            addResponseToSession(
              sessionId,
              toResponseHistoryEntry({
                replayResponse,
                requestData: historicalRequestData,
                credential: {
                  id: credential.id,
                  username: credential.username,
                  role: credential.role,
                },
                sentRequestWire,
                sentRequestWireEncoding,
              }),
            );
          } catch (error) {
            addResponseToSession(
              sessionId,
              createErrorResponse({
                error: error instanceof Error ? error.message : 'Unknown error',
                requestData: historicalRequestData,
                credential: {
                  id: credential.id,
                  username: credential.username,
                  role: credential.role,
                },
              }),
            );
          }
        }
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [
      sessionId,
      sendSingleRequest,
      addResponseToSession,
      runPreRequestScript,
      scriptVariables,
      updateSessionProtocol,
    ],
  );

  const handleReset = useCallback(() => {
    const sess = sessionRef.current;

    if (!sess) return;

    const req = sess.request;
    const hasOriginalHeaders =
      req.headers && Object.keys(req.headers).length > 0;

    if (hasOriginalHeaders) {
      updateSessionRequest(sessionId, formatRequestToText(req));

      return;
    }

    const firstResponse = sess.responseHistory[0];

    if (firstResponse?.requestData) {
      updateSessionRequest(sessionId, firstResponse.requestData);

      return;
    }

    const parsed = parseRequestFromText({
      text: sess.requestData,
      originalUrl: req.url,
    });

    if (!parsed) {
      toasterApi.error({
        title: 'Failed to reset request',
        description: 'Could not parse the request. Check the request format.',
      });

      return;
    }

    updateSessionRequest(
      sessionId,
      formatRequestToText({
        method: req.method,
        url: req.url,
        headers: parsed.headers,
        body: req.body || '',
        httpVersion: parsed.httpVersion,
      }),
    );
  }, [sessionId, updateSessionRequest]);

  const handleFollowRedirect = useCallback(async () => {
    const sess = sessionRef.current;

    if (!sess) return;
    if (isLoadingRef.current) return;

    const response = sess.responseHistory[sess.currentResponseIndex];

    if (!response) return;

    const locationUrl = getLocationHeader(response.headers);

    if (!locationUrl) return;

    const currentRequest = parseRequestFromText({
      text: sess.requestData,
      originalUrl: sess.request.url,
    });

    if (!currentRequest) {
      toasterApi.error({
        title: 'Failed to follow redirect',
        description: 'Could not parse the request. Check the request format.',
      });

      return;
    }

    const newRequest = buildRedirectRequest({
      currentRequest,
      redirectUrl: locationUrl,
      statusCode: response.statusCode,
    });

    const redirectProtocol = replayProtocolFromHttpVersion(
      currentRequest.httpVersion,
    );
    const currentSession = sessionRef.current ?? sess;

    if (redirectProtocol !== currentSession.protocol) {
      updateSessionProtocol(sessionId, redirectProtocol);
    }

    const replaySession: ReplaySession = {
      ...currentSession,
      protocol: redirectProtocol,
    };

    let historicalRequestData = formatRequestToText(newRequest);

    updateSessionRequest(sessionId, historicalRequestData);

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const activeAppCredential = sess.activeCredential
        ? applicationCredentialsRef.current.find(
            (c) => c.id === sess.activeCredential?.id,
          )
        : undefined;
      const sandboxCreds = activeAppCredential
        ? buildSandboxCredential(activeAppCredential)
        : undefined;
      const replayMtls = buildReplayMtlsPayload(activeAppCredential);

      const { request: finalRequest } = await runPreRequestScript({
        parsedRequest: newRequest,
        credentials: sandboxCreds,
      });

      historicalRequestData = formatRequestToText(finalRequest);

      const {
        response: replayResponse,
        sentRequestWire,
        sentRequestWireEncoding,
      } = await sendSingleRequest({
        session: replaySession,
        parsedRequest: finalRequest,
        requestData: historicalRequestData,
        originalUrl: sess.request.url,
        name: 'Follow Redirect',
        replayMtls,
      });

      addResponseToSession(
        sessionId,
        toResponseHistoryEntry({
          replayResponse,
          requestData: historicalRequestData,
          sentRequestWire,
          sentRequestWireEncoding,
        }),
      );
    } catch (error) {
      addResponseToSession(
        sessionId,
        createErrorResponse({
          error: error instanceof Error ? error.message : 'Unknown error',
          requestData: historicalRequestData,
        }),
      );
    } finally {
      isLoadingRef.current = false;
      setIsLoading(false);
    }
  }, [
    sessionId,
    sendSingleRequest,
    addResponseToSession,
    updateSessionRequest,
    updateSessionProtocol,
    runPreRequestScript,
  ]);

  const handleNavigateResponse = useCallback(
    (direction: 'prev' | 'next') => {
      const sess = sessionRef.current;

      if (!sess) return;
      const delta = direction === 'prev' ? -1 : 1;
      const newIndex = sess.currentResponseIndex + delta;

      if (newIndex < 0 || newIndex >= sess.responseHistory.length) return;
      const historicalRequest = sess.responseHistory[newIndex]?.requestData;

      if (historicalRequest) {
        updateSessionRequest(sessionId, historicalRequest);
      }

      setSessionResponseIndex(sessionId, newIndex);
    },
    [sessionId, setSessionResponseIndex, updateSessionRequest],
  );

  // Register keyboard shortcut
  useEffect(() => {
    return registerReplayTrigger({ sessionId, send: handleReplay });
  }, [sessionId, handleReplay]);

  // Register Esc key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' && isLoading && abortControllerRef.current) {
        event.preventDefault();
        handleReplay();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLoading, handleReplay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Load sandbox status
  useEffect(() => {
    let cancelled = false;
    const loadSandboxStatus = async () => {
      if (
        typeof window === 'undefined' ||
        !window.electronAPI?.replay?.getPreRequestSandboxStatus
      ) {
        return;
      }

      try {
        const status =
          await window.electronAPI.replay.getPreRequestSandboxStatus();

        if (!cancelled) setPreRequestSandboxStatus(status);
      } catch {
        if (!cancelled) {
          setPreRequestSandboxStatus({
            ready: false,
            platform: 'unknown',
            reason: 'Unable to load sandbox status',
            allowUnsafeFallback: false,
            isPackaged: false,
          });
        }
      }
    };

    void loadSandboxStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    session,
    isLoading,
    preRequestSandboxStatus,
    scriptEnabled,
    setActivePresetId,
    handleReplay,
    handleRunAllCredentials,
    handleReset,
    handleFollowRedirect,
    handleNavigateResponse,
    updateSessionRequest,
    updateSessionPreserveContentLength,
    updateSessionConnectionTarget,
    updateSessionActiveCredential,
    updateSessionAutoFollowRedirects,
    updateSessionSyncTargetWithHostHeader,
  };
}
